import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { LogOut } from "lucide-react";

import { ModeToggle } from "@/components/mode-toggle";
import { Button } from "@/components/ui/button";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { api } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { useEstaEnElModulo } from "@/lib/modulo";
import { mesEnLetras } from "@/lib/periodo";
import { cn } from "@/lib/utils";

// ===========================================================================
// La barra superior, con el patrón del ERP: el plegador a la izquierda y las
// acciones a la derecha.
//
// En medio, y SOLO dentro del módulo de conciliación, va lo que la propuesta
// pone en su `tb-org`: en qué periodo se trabaja y si está abierto o cerrado.
//
// Es condicional a propósito. Cuando este módulo se mude al ERP, su barra la
// comparten veinte módulos y el periodo de conciliación no pinta nada mientras
// alguien mira una planilla.
//
// El `tb-busca` de la lámina no está: se dibujó como un campo que no buscaba y
// se quitó. Vuelve cuando haya qué buscar.
// ===========================================================================

export function Topbar() {
  const { data: sesion } = authClient.useSession();
  const enElModulo = useEstaEnElModulo();
  const navigate = useNavigate();
  const qc = useQueryClient();

  /**
   * CERRAR SESIÓN TIENE QUE LLEVAR AL LOGIN, no solo borrar la cookie.
   *
   * `signOut()` a secas la borraba en el servidor y la pantalla se quedaba
   * igual: los mismos números, el mismo menú, todo pintado desde la caché de
   * React Query. El guard de `/_app` solo corre en `beforeLoad`, así que sin
   * navegar nadie vuelve a preguntar por la sesión, y el botón parecía roto.
   * Peor: en una máquina compartida, quien llegaba después seguía viendo los
   * datos del anterior hasta recargar.
   *
   * Se navega SIEMPRE, aunque la llamada falle: si el servidor no contesta, la
   * salida no puede quedar a medias. Y se vacía la caché, que es lo que tenía
   * dentro las cifras del mes.
   */
  const salir = async () => {
    try {
      await authClient.signOut();
    } finally {
      qc.clear();
      await navigate({ to: "/login" });
    }
  };

  // La consulta solo se lanza dentro del módulo: fuera no hay periodo que
  // enseñar y no hay por qué pedirlo.
  const { data } = api.contexto.useQuery(["contexto"], undefined, {
    enabled: enElModulo,
    staleTime: 60_000,
  });
  const ctx = data?.status === 200 ? data.body : null;

  return (
    <header className="flex h-14 items-center gap-3 border-b px-4">
      <SidebarTrigger />

      {enElModulo && ctx ? (
        <>
          <div className="hidden shrink-0 items-center gap-1.5 text-[12.5px] text-muted-foreground sm:flex">
            Grupo Ecoplaza
            <span className="text-border">·</span>
            <span className="text-foreground">{mesEnLetras(ctx.periodo)}</span>
            <span className="text-border">·</span>
            {/* Abierto o cerrado, con su palabra: de esto depende si se puede
                tocar algo, así que no puede ser solo un color. */}
            <span
              className={cn(
                "rounded-full border px-2 py-0.5 font-semibold text-[11px]",
                ctx.estado === "abierto"
                  ? "border-emerald-600/30 bg-emerald-600/10 text-emerald-700 dark:text-emerald-400"
                  : "border-border bg-muted text-muted-foreground",
              )}
            >
              {ctx.estado}
            </span>
          </div>

          {/* AQUÍ IBA UN BUSCADOR QUE NO BUSCABA.
              Un `<input>` habilitado, sin `value`, sin `onChange` y sin nada
              detrás, en la barra de TODAS las pantallas. Se podía escribir en
              él y no pasaba nada, que es la peor forma de no funcionar: la
              deshabilitada al menos se nota.

              Se quita hasta que haya una búsqueda de verdad. La que hace falta
              cruza vouchers, operaciones y clientes, y hoy `mesa` solo acepta
              un `buscar` de texto sobre movimientos; el buscador global llega
              con los filtros de la mesa. Mientras tanto, el que sí funciona
              está dentro de `/movimientos` y de `/empresas`. */}
        </>
      ) : null}

      <div className="ml-auto flex shrink-0 items-center gap-2">
        {sesion?.user ? (
          <span className="hidden text-muted-foreground text-sm lg:inline">
            {sesion.user.name}
          </span>
        ) : null}
        <ModeToggle />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => void salir()}
          aria-label="Cerrar sesión"
        >
          <LogOut className="size-4" />
        </Button>
      </div>
    </header>
  );
}
